import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSwipeable } from 'react-swipeable';

import PropTypes from 'prop-types';

import { useApp } from 'hooks/selectors';

import PopupPortal from './PopupPortal';
import { getoutAnimation, Overlay, PopupBox, PopupBoxInner, PopupOuter } from './styles';

const PopupNew = React.forwardRef(function PopupNew(props, innerRef) {
    const { isShowSpinner, isShowModal } = useApp();
    const overlayRef = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const [state, setState] = useState({
        isShow: false,
        animationEnd: false,
    });
    const [data, setData] = useState(null);
    const [bottom, setBottom] = useState('');
    const { as: Wrapper = PopupPortal } = props;

    useImperativeHandle(
        innerRef,
        () => ({
            open: (data = null) => {
                setOpen(true);
                if (data) {
                    setData(data);
                }
            },
            close: () => setOpen(false),
        }),
        [],
    );

    useEffect(() => {
        if (__isBrowser__ && isOpen) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', keyPressHandler);
        }
        return () => {
            if (__isBrowser__ && isOpen && !isShowModal && !props.overflowHidden) {
                document.body.style.overflow = 'visible';
                document.removeEventListener('keydown', keyPressHandler);
            }
        };
    }, [isShowModal, isOpen]);

    useEffect(() => {
        if (isOpen) {
            window.visualViewport?.addEventListener('resize', checkKeyboardOpened);
            setState((prevState) => {
                return {
                    ...prevState,
                    isShow: true,
                };
            });
        } else if (!isOpen && state.isShow) {
            setState((prevState) => {
                return {
                    ...prevState,
                    animationEnd: true,
                };
            });
        }
        return () => {
            window.visualViewport?.removeEventListener('resize', checkKeyboardOpened);
            setBottom('');
        };
    }, [isOpen]);

    const checkKeyboardOpened = () => {
        const initialScreenSize = document.body.clientHeight;
        const viewportHeight = window.visualViewport.height;
        const offset = window.visualViewport.offsetTop;
        const bottom = `${initialScreenSize - viewportHeight - offset + 24}px`;
        const isKeyboardOpened = viewportHeight < initialScreenSize;
        setBottom(isKeyboardOpened ? bottom : '');
    };

    const slide = () => {
        setOpen(false);
    };

    const handlers = useSwipeable({
        onSwipedDown: () => slide(),
        delta: 20,
        preventDefaultTouchmoveEvent: true,
        trackTouch: true,
    });

    const keyPressHandler = (e) => {
        if (e.keyCode === 27) {
            e.preventDefault();
            setOpen(false);
        }
    };

    const overlayClick = (e) => {
        if (overlayRef.current && !overlayRef.current.contains(e.target)) {
            if (data?.props?.onOverlayClick) {
                data.props.onOverlayClick(e);
            }
            e.preventDefault();
            setOpen(false);
        }
    };

    const closeAnimationPopup = useCallback((e) => {
        if (e?.animationName === getoutAnimation?.name) {
            flushSync(() => {
                setState({
                    isShow: false,
                    animationEnd: false,
                });
                setData(null);
            });
        }
    }, []);

    const animated = useMemo(() => !isOpen && state.animationEnd, [isOpen, state.animationEnd]);

    if (state.isShow) {
        return (
            <Wrapper>
                <div id="popup-body" {...handlers}>
                    <Overlay onClick={overlayClick}>
                        <PopupOuter fullwidthPopup={props.fullwidthPopup}>
                            <PopupBox animated={animated} onAnimationEnd={closeAnimationPopup} bottom={bottom} fullwidthPopup={props.fullwidthPopup}>
                                <PopupBoxInner ref={overlayRef} isShowSpinner={isShowSpinner} fullwidthPopup={props.fullwidthPopup}>
                                    {props.getPopupComponent?.(data) || data}
                                </PopupBoxInner>
                            </PopupBox>
                        </PopupOuter>
                    </Overlay>
                </div>
            </Wrapper>
        );
    } else {
        return null;
    }
});
PopupNew.propTypes = {
    getPopupComponent: PropTypes.func,
    as: PropTypes.func,
    overflowHidden: PropTypes.bool,
    onOverlayClick: PropTypes.func,
    fullwidthPopup: PropTypes.bool,
};
export default PopupNew;
